import { useTheme } from '../../../hooks/useTheme';
import {
  DARK_RING_SHADOW,
  DOCK_ICON_COLOR,
  RING_PROGRESS_END,
  RING_PROGRESS_TOP,
  RING_SHADOW,
  type ActionDockRingConfig,
} from './constants';

/** Circular "NN% set up" progress ring (Figma node 3062-6277), extracted 1:1
 *  from the /cv bottom bar's web SetupRing. */
export function ActionDockRing({
  completionPercent,
  label,
  a11yLabel,
  a11yHint,
  onPress,
}: ActionDockRingConfig) {
  const { theme, isDark } = useTheme();
  const safePercent = Math.max(0, Math.min(100, Math.round(completionPercent)));
  const size = 65;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (safePercent / 100) * circumference;
  const ringTextColor = isDark ? theme.text : 'var(--text-primary)';
  const ringLabelColor = isDark ? theme.textSecondary : DOCK_ICON_COLOR;

  return (
    <div
      className="action-dock-setup-ring"
      role="button"
      tabIndex={0}
      aria-label={a11yLabel}
      title={a11yHint}
      onClick={onPress}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress();
        }
      }}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        background: isDark ? theme.surfaceElevated : '#FFFFFF',
        boxShadow: isDark ? DARK_RING_SHADOW : RING_SHADOW,
        border: `1px solid ${isDark ? theme.borderStrong : 'rgba(0,0,0,0.04)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: 'pointer',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <defs>
          <linearGradient
            id="actionDockRingProgressWeb"
            x1="0"
            y1="0"
            x2={size}
            y2={size}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor={RING_PROGRESS_TOP} />
            <stop offset="1" stopColor={RING_PROGRESS_END} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={isDark ? theme.borderStrong : '#ECECEC'}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#actionDockRingProgressWeb)"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1.8 }}>
          <span
            style={{
              fontSize: 18,
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: 0,
              color: ringTextColor,
            }}
          >
            {safePercent}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '13.5px',
              marginBottom: 1,
              color: ringTextColor,
            }}
          >
            %
          </span>
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            letterSpacing: 0,
            color: ringLabelColor,
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}
